import {useCallback, useEffect, useState} from "react";
import useEnterKey from "./useEnterKey";

const MODAL_BUTTON_SELECTOR = 'button:not([disabled]):not(.close)';
const NAVIGATION_KEYS = ["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"];

const focusButton = (button, {center = false} = {}) => {
    if (!button) return;

    button.focus({preventScroll: true});

    if (center && button.scrollIntoView) {
        button.scrollIntoView({
            block: "center",
            inline: "nearest",
            behavior: "smooth"
        });
    }
};

const isTypingTarget = target => {
    if (!target) return false;
    const tagName = target.tagName;
    if (target.isContentEditable) return true;
    if (tagName === "TEXTAREA" || tagName === "SELECT") return true;
    if (tagName !== "INPUT") return false;

    const inputType = (target.type || "").toLowerCase();
    return !["button", "checkbox", "radio", "submit", "reset"].includes(inputType);
};

const overlapSize = (startA, endA, startB, endB) => {
    return Math.max(0, Math.min(endA, endB) - Math.max(startA, startB));
};

const isFooterElement = element => {
    return !!element?.closest?.(".modal-footer");
};

const getDirectionalNextIndex = (elements, currentIndex, key) => {
    const currentElement = elements[currentIndex];
    if (!currentElement) return currentIndex;

    const currentRect = currentElement.getBoundingClientRect();
    const currentCenterX = currentRect.left + currentRect.width / 2;
    const currentCenterY = currentRect.top + currentRect.height / 2;

    const isHorizontalMove = key === "ArrowLeft" || key === "ArrowRight";

    const currentIsFooter = isFooterElement(currentElement);

    const candidates = elements
        .map((element, index) => {
            if (index === currentIndex) return null;

            const rect = element.getBoundingClientRect();
            const centerX = rect.left + rect.width / 2;
            const centerY = rect.top + rect.height / 2;

            const isCandidate =
                (key === "ArrowLeft" && centerX < currentCenterX) ||
                (key === "ArrowRight" && centerX > currentCenterX) ||
                (key === "ArrowUp" && centerY < currentCenterY) ||
                (key === "ArrowDown" && centerY > currentCenterY);

            if (!isCandidate) return null;

            const primaryDistance = isHorizontalMove
                ? Math.abs(centerX - currentCenterX)
                : Math.abs(centerY - currentCenterY);
            const secondaryDistance = isHorizontalMove
                ? Math.abs(centerY - currentCenterY)
                : Math.abs(centerX - currentCenterX);

            const laneOverlap = isHorizontalMove
                ? overlapSize(currentRect.top, currentRect.bottom, rect.top, rect.bottom)
                : overlapSize(currentRect.left, currentRect.right, rect.left, rect.right);
            const sameLane = laneOverlap > 0;

            return {
                index,
                primaryDistance,
                secondaryDistance,
                sameLane,
                laneOverlap,
                isFooter: isFooterElement(element)
            };
        })
        .filter(Boolean);

    if (!candidates.length) return currentIndex;

    const nonFooterCandidates = candidates.filter(candidate => !candidate.isFooter);
    const scopedCandidates = currentIsFooter || !nonFooterCandidates.length
        ? candidates
        : nonFooterCandidates;

    if (isHorizontalMove) {
        const sameLaneCandidates = scopedCandidates.filter(candidate => candidate.sameLane);
        if (!sameLaneCandidates.length) {
            return currentIndex;
        }
    }

    scopedCandidates.sort((a, b) => {
        if (isHorizontalMove) {
            if (a.sameLane !== b.sameLane) {
                return a.sameLane ? -1 : 1;
            }
            if (a.primaryDistance !== b.primaryDistance) {
                return a.primaryDistance - b.primaryDistance;
            }
            return a.secondaryDistance - b.secondaryDistance;
        }

        if (a.primaryDistance !== b.primaryDistance) {
            return a.primaryDistance - b.primaryDistance;
        }
        if (a.secondaryDistance !== b.secondaryDistance) {
            return a.secondaryDistance - b.secondaryDistance;
        }
        return b.laneOverlap - a.laneOverlap;
    });

    return scopedCandidates[0].index;
};

const useModalKeyboardNavigation = ({
    modalId,
    enabled = true,
    dependencies = [],
    buttonSelector = MODAL_BUTTON_SELECTOR
}) => {
    const [selectedButtonIndex, setSelectedButtonIndex] = useState(0);

    const getModalButtons = useCallback(() => {
        const modal = document.getElementById(modalId);
        if (!modal) return [];

        return Array.from(modal.querySelectorAll(buttonSelector));
    }, [modalId, buttonSelector]);

    useEffect(() => {
        if (!enabled) return;
        setSelectedButtonIndex(0);
    }, [enabled, modalId]);

    useEffect(() => {
        if (!enabled) return;

        const modal = document.getElementById(modalId);
        if (!modal) return;

        const buttons = getModalButtons();
        if (!buttons.length) return;

        const safeIndex = Math.min(selectedButtonIndex, buttons.length - 1);
        if (safeIndex !== selectedButtonIndex) {
            setSelectedButtonIndex(safeIndex);
            return;
        }

        const activeElement = document.activeElement;
        const activeIsInsideModal = activeElement && modal.contains(activeElement);
        if (activeIsInsideModal) {
            const activeIndex = buttons.indexOf(activeElement);
            if (activeIndex >= 0 && activeIndex !== selectedButtonIndex) {
                setSelectedButtonIndex(activeIndex);
            }
            return;
        }

        const selectedButton = buttons[safeIndex];
        if (selectedButton && document.activeElement !== selectedButton) {
            focusButton(selectedButton);
        }
    }, [enabled, modalId, getModalButtons, selectedButtonIndex, ...dependencies]);

    useEffect(() => {
        if (!enabled) return;

        const handleKeyDown = e => {
            if (!enabled) return;
            if (!NAVIGATION_KEYS.includes(e.key)) return;
            if (isTypingTarget(e.target)) return;

            const buttons = getModalButtons();
            if (!buttons.length) return;

            const activeIndex = buttons.indexOf(document.activeElement);
            const fallbackIndex = Math.min(selectedButtonIndex, buttons.length - 1);
            const currentIndex = activeIndex >= 0 ? activeIndex : fallbackIndex;
            const nextIndex = getDirectionalNextIndex(buttons, currentIndex, e.key);
            const nextButton = buttons[nextIndex];

            setSelectedButtonIndex(nextIndex);
            if (nextButton) {
                focusButton(nextButton, {center: true});
            }
            e.preventDefault();
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [enabled, getModalButtons, selectedButtonIndex]);

    useEnterKey(
        () => {
            if (!enabled) return;
            if (isTypingTarget(document.activeElement)) return;

            const buttons = getModalButtons();
            if (!buttons.length) return;

            const activeIndex = buttons.indexOf(document.activeElement);
            const fallbackIndex = Math.min(selectedButtonIndex, buttons.length - 1);
            const indexToClick = activeIndex >= 0 ? activeIndex : fallbackIndex;
            const selectedButton = buttons[indexToClick];

            if (selectedButton) {
                focusButton(selectedButton);
                selectedButton.click();
            }
        },
        enabled,
        true
    );
};

export default useModalKeyboardNavigation;
